/**
 * MeetingDetailPanel — Slide-in sheet for viewing past meeting details
 */

import DOMPurify from 'dompurify';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { SlidePanel } from '@/shared/components';
import { ModalErrorBoundary } from '@/components/ErrorBoundary';
import { useMeeting } from '../hooks/useMeetings';
import { MeetingRecordingPlayer } from './MeetingRecordingPlayer';
import { MeetingTranscriptViewer } from './MeetingTranscriptViewer';
import { CrmCompanyChip } from './CrmCompanyChip';

// =============================================================================
// Types
// =============================================================================

interface MeetingDetailPanelProps {
  meetingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// =============================================================================
// Helpers
// =============================================================================

function formatSectionDuration(seconds: number | null): string {
  const s = seconds ?? 0;
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

// =============================================================================
// Sub-components
// =============================================================================

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      {children}
    </div>
  );
}

function AmberBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
      {message}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4 px-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MeetingDetailPanel
// =============================================================================

export function MeetingDetailPanel({ meetingId, open, onOpenChange }: MeetingDetailPanelProps) {
  const { data: meeting, isLoading } = useMeeting(meetingId ?? undefined);

  const tickets = meeting?.tickets ?? [];
  const solvedTickets = tickets.filter((t) => t.action === 'solved');
  const solvedCount = solvedTickets.length;
  const totalCount = tickets.length;
  const solveRate =
    totalCount > 0 ? `${((solvedCount / totalCount) * 100).toFixed(0)}%` : '0%';

  return (
    <SlidePanel open={open} storageKey="atek_meeting_panel_width">
      {/* Fixed header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border shrink-0">
        <h2 className="text-lg font-semibold">
          {meeting ? `Past ${meeting.title}` : 'Meeting Detail'}
        </h2>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading || (!meeting && meetingId) ? (
            /* Loading skeleton */
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-10 rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : !meeting ? (
            <p className="text-sm text-muted-foreground">Meeting not found.</p>
          ) : (
            <>
              {/* 1. To-Dos Created */}
              <Section label="To-Dos Created">
                <AmberBanner message="No To-Dos were created during this Meeting" />
              </Section>

              {/* 2. Issues Solved */}
              <Section label="Issues Solved">
                {solvedCount === 0 ? (
                  <AmberBanner message="No Issues were solved during this Meeting" />
                ) : (
                  <p className="text-sm font-medium">
                    {solvedCount} issue{solvedCount !== 1 ? 's' : ''} solved
                  </p>
                )}
              </Section>

              {/* 3. Short-Term Issues Stats */}
              <Section label="Short-Term Issues Stats">
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="Total Tracked Issues" value={totalCount} />
                  <StatCard label="Issues Solved Last Meeting" value="—" />
                  <StatCard label="Issues Solved Today" value={solvedCount} />
                  <StatCard label="Solve Rate" value={solveRate} />
                </div>
              </Section>

              {/* 4. Meeting Notes */}
              <Section label="Meeting Notes">
                {meeting.notes ? (
                  <div
                    className="prose prose-sm max-w-none text-sm text-foreground"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meeting.notes) }}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No meeting notes</p>
                )}
              </Section>

              {/* 4b. Recording */}
              <Section label="Recording">
                {meeting.recording_url ? (
                  <ModalErrorBoundary>
                    <MeetingRecordingPlayer meetingId={meeting.id} />
                  </ModalErrorBoundary>
                ) : (
                  <p className="text-sm text-muted-foreground">No recording yet</p>
                )}
              </Section>

              {/* 4c. Transcript */}
              <Section label="Transcript">
                <ModalErrorBoundary>
                  <MeetingTranscriptViewer
                    meetingId={meeting.id}
                    hasTranscript={!!meeting.transcript_url}
                  />
                </ModalErrorBoundary>
              </Section>

              {/* 4d. CRM */}
              <Section label="CRM Company">
                <CrmCompanyChip
                  matchStatus={meeting.match_status}
                  matchedCompany={meeting.matched_company}
                />
              </Section>

              {/* 4e. Attendees / Contacts */}
              {meeting.matched_contacts.length > 0 && (
                <Section label="Matched Contacts">
                  <div className="flex flex-wrap gap-1.5">
                    {meeting.matched_contacts.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center rounded-full bg-muted border border-border px-2.5 py-0.5 text-xs"
                        title={c.email}
                      >
                        {c.name}
                      </span>
                    ))}
                  </div>
                </Section>
              )}

              {/* 5. Ratings */}
              <Section label="Ratings">
                {meeting.attendees.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attendees recorded</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                            Attendee
                          </th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                            Rating
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {meeting.attendees.map((attendee) => (
                          <tr key={attendee.id} className="border-b last:border-0">
                            <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                              {attendee.user_id}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {attendee.rating !== null ? (
                                <span className="font-medium">{attendee.rating} / 10</span>
                              ) : (
                                <span className="text-muted-foreground">N/A</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* 6. Section Durations */}
              <Section label="Section Durations">
                {meeting.sections.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sections recorded</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                            Section
                          </th>
                          <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {meeting.sections.map((section) => (
                          <tr key={section.id} className="border-b last:border-0">
                            <td className="px-3 py-2">{section.name}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs">
                              {formatSectionDuration(section.duration_actual)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </>
          )}
      </div>
    </SlidePanel>
  );
}
